// src/generators/dzial2_algebra/temat3_nierownosci/podtemat3_wartosc_bezwzgledna_types/AbsLinearInequalityShiftedScaledAllCasesTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";
import TwoRangesOverlap from "../../../../components/visuals/TwoRangesOverlap";

/* ================= helpers ================= */
const SIGN_KINDS = ["lt", "gt", "le", "ge"]; // < > ≤ ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };
const invertSign = (k) => ({ lt: "gt", gt: "lt", le: "ge", ge: "le" }[k]);

function absLatex(A, B) {
  const ax = A === 1 ? "x" : A === -1 ? "-x" : A > 0 ? `${A}x` : `-${Math.abs(A)}x`;
  const bx = B === 0 ? "" : B > 0 ? `+ ${B}` : `- ${Math.abs(B)}`;
  return `\\left|${ax} ${bx}\\right|`.replace(/\s+/g, " ").trim();
}

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; }
function toFrac(num, den) { if (den < 0) { num = -num; den = -den; } const g = gcd(num, den); return { n: num / g, d: den / g }; }
function fracToLatex({ n, d }) {
  const s = n < 0 ? "-" : "";
  const an = Math.abs(n);
  const int = Math.floor(an / d);
  const rem = an % d;
  if (rem === 0) return `${s}${int}`;
  return int === 0 ? `${s}\\dfrac{${rem}}{${d}}` : `${s}${int}\\dfrac{${rem}}{${d}}`;
}
function orderedBounds(A, B, c) {
  const vL = (-c - B) / A, vR = (c - B) / A;
  const texL = fracToLatex(toFrac(-c - B, A));
  const texR = fracToLatex(toFrac(c - B, A));
  return vL <= vR ? { lowNum: vL, highNum: vR, lowTex: texL, highTex: texR }
                  : { lowNum: vR, highNum: vL, lowTex: texR, highTex: texL };
}

/* ===== rozwiązanie ogólne (c może być <0/0/>0) ===== */
function solutionForAll({ A, B, c, kind }) {
  const x0 = -B / A;
  if (c < 0) { if (kind === "lt" || kind === "le") return { kind: "empty" }; return { kind: "all" }; }
  if (c === 0) {
    if (kind === "lt") return { kind: "empty" };
    if (kind === "le") return { kind: "point", x0 };
    if (kind === "gt") return { kind: "outsideHole", x0 };
    return { kind: "all" };
  }
  if (kind === "lt" || kind === "le") {
    const L = (-c - B) / A, R = (c - B) / A, inc = kind === "le";
    return { kind: "between", seg: { L: Math.min(L, R), R: Math.max(L, R), Lclosed: inc, Rclosed: inc } };
  }
  const inc = kind === "ge";
  const b1 = ( c - B) / A;
  const b2 = (-c - B) / A;
  const r1 = Math.min(b1, b2);
  const r2 = Math.max(b1, b2);
  return { kind: "outside", inclusive: inc, left: { L: -Infinity, R: r1, Rclosed: inc }, right: { L: r2, R: Infinity, Lclosed: inc } };
}

/* ================= generator ================= */
export function generateTask() {
  const randi = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
  const pick = (arr) => arr[randi(0, arr.length - 1)];
  let A = 0; while (A === 0) A = randi(-8, 8);
  const B = randi(-12, 12);
  const alpha = pick([-7,-6,-5,-4,-3,-2, 2,3,4,5,6,7]);
  let beta = 0; while (beta === 0) beta = randi(-12, 12);
  const kind = pick(SIGN_KINDS);
  const signBucket = pick(["neg","zero","pos"]);
  const c_final = signBucket === "zero" ? 0 : signBucket === "neg" ? -randi(1, 6) : randi(1, 10);
  const gamma = alpha * c_final + beta;
  const kind_init = alpha >= 0 ? kind : invertSign(kind);
  return { type: "AbsLinearInequalityShiftedScaledAllCasesTask", data: { A, B, alpha, beta, gamma, kind_init, kind, c: c_final } };
}

/* ================= UI ================= */
export function renderTask({ data }) {
  const { A, B, alpha, beta, gamma, kind_init } = data;
  const left = `${alpha}\\,${absLatex(A,B)} ${beta===0?"":beta>0?`+ ${beta}`:`- ${Math.abs(beta)}`}`;
  const latex = `${left}\\;${SIGN_LATEX[kind_init]}\\;${gamma}`;
  return (<p>Rozwiąż nierówność: <InlineMath math={latex} /></p>);
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}><InlineMath math={'x\\;\\in'} /></div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 2) ∪ (5, ∞) • (-3, 7) • [1, 4] • {3} • ℝ • ∅"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw = typeof userInput === "string" ? userInput : userInput?.S?.raw || userInput?.S || "";
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

export function checkAnswer({ data }, userInput = {}) {
  const raw = typeof userInput?.S === "string" ? userInput.S : userInput?.S?.raw || "";
  if (!raw) return { correct: false };
  const parsed = parseIntervalInput(raw);
  const ans = solutionForAll({ A: data.A, B: data.B, c: data.c, kind: data.kind });

  function intervalsEqualFlexible(expected, parsed) {
    if (!parsed) return false;
    if (expected.kind === "all") {
      return parsed.kind === "all" ||
        (parsed.kind === "intervals" && parsed.intervals.length === 1 &&
         parsed.intervals[0].left === -Infinity && parsed.intervals[0].right === Infinity);
    }
    if (expected.kind === "empty") {
      return parsed.kind === "empty" || (parsed.kind === "intervals" && parsed.intervals.length === 0);
    }
    if (expected.kind === "point") {
      if (parsed.kind === "point") return Math.abs(parsed.value - expected.x0) <= 1e-9;
      if (parsed.kind === "intervals" && parsed.intervals.length === 1) {
        const I = parsed.intervals[0];
        return I.leftClosed && I.rightClosed &&
               Math.abs(I.left - expected.x0) <= 1e-9 &&
               Math.abs(I.right - expected.x0) <= 1e-9;
      }
      return false;
    }
    if (expected.kind === "outsideHole") {
      if (parsed.kind === "intervals" && parsed.intervals.length === 2) {
        const [A1, B1] = parsed.intervals;
        const aOK = A1.left === -Infinity && Math.abs(A1.right - expected.x0) <= 1e-9 && !A1.rightClosed;
        const bOK = B1.right === Infinity && Math.abs(B1.left - expected.x0) <= 1e-9 && !B1.leftClosed;
        return aOK && bOK;
      }
      return false;
    }
    if (parsed.kind !== "intervals") return false;
    const eq = (x, y, tol = 1e-9) => Math.abs(x - y) <= tol;
    const expectList = [];
    if (expected.kind === "outside") {
      expectList.push({ L: -Infinity, R: expected.left.R, Lc:false, Rc:!!expected.left.Rclosed });
      expectList.push({ L: expected.right.L, R: Infinity, Lc:!!expected.right.Lclosed, Rc:false });
    } else if (expected.kind === "between") {
      expectList.push({ L: expected.seg.L, R: expected.seg.R, Lc:!!expected.seg.Lclosed, Rc:!!expected.seg.Rclosed });
    } else return false;

    const got = parsed.intervals.map(I => ({ L: I.left, R: I.right, Lc: !!I.leftClosed, Rc: !!I.rightClosed }));
    if (expectList.length !== got.length) return false;
    const used = new Array(got.length).fill(false);
    for (const E of expectList) {
      let ok = false;
      for (let i = 0; i < got.length; i++) {
        if (used[i]) continue;
        const G = got[i];
        const Lok = (E.L === -Infinity ? G.L === -Infinity : eq(E.L, G.L));
        const Rok = (E.R ===  Infinity ? G.R ===  Infinity : eq(E.R, G.R));
        if (Lok && Rok && E.Lc === G.Lc && E.Rc === G.Rc) { used[i] = true; ok = true; break; }
      }
      if (!ok) return false;
    }
    return true;
  }
  return { correct: intervalsEqualFlexible(ans, parsed) };
}

/* ===== poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const { A, B, c, kind } = data;
  const ans = solutionForAll({ A, B, c, kind });
  const { lowTex, highTex } = orderedBounds(A, B, c);
  const x0Tex = fracToLatex(toFrac(-B, A));
  if (ans.kind === "all")   return <InlineMath math={'x\\in \\mathbb{R}'} />;
  if (ans.kind === "empty") return <InlineMath math={'x\\in \\varnothing'} />;
  if (ans.kind === "point") return <InlineMath math={`x\\in \\{${x0Tex}\\}`} />;
  if (ans.kind === "outsideHole") return <InlineMath math={`x\\in (-\\infty,\\; ${x0Tex})\\;\\cup\\;(${x0Tex},\\; \\infty)`} />;
  if (ans.kind === "between") {
    const lb = ans.seg.Lclosed ? "[" : "("; const rb = ans.seg.Rclosed ? "]" : ")";
    return <InlineMath math={`x\\in ${lb}${lowTex},\\; ${highTex}${rb}`} />;
  }
  const rb = ans.inclusive ? "]" : ")"; const lb = ans.inclusive ? "[" : "(";
  return <InlineMath math={`x\\in (-\\infty,\\; ${lowTex}${rb}\\;\\cup\\; ${lb}${highTex},\\; \\infty)`} />;
}

/* ===== kroki/wyjaśnienie ===== */

// Nierówność liniowa (Warunek 1/2)
function solveLinearSide(A, B, rel, c) {
  const ax = A === 1 ? "x" : A === -1 ? "-x" : `${A}x`;
  const btxt = B === 0 ? "" : B > 0 ? `+ ${B}` : `- ${Math.abs(B)}`;

  const opB = B === 0 ? "" : String.raw`\;\Big|\, ${B > 0 ? "-" : "+"}\, ${Math.abs(B)}`;
  const line1 = String.raw`${ax} ${btxt} \; ${rel} \; ${c}${opB}`;

  const rhs2 = B === 0 ? c : (B > 0 ? `${c - B}` : `${c + Math.abs(B)}`);
  const line2 = String.raw`${ax} \; ${rel} \; ${rhs2}\;\Big|:\, ${A}`;

  const flip = A < 0;
  const rel3 = flip ? (rel === "<" ? ">" : rel === ">" ? "<" : rel === "≤" ? "≥" : "≤") : rel;
  const { n, d } = toFrac(Number(rhs2), A);
  const line3 = String.raw`x \; ${rel3} \; ${fracToLatex({ n, d })}`;

  return String.raw`\begin{aligned}
${line1}\\[2pt]
${line2}\\[2pt]
${line3}
\end{aligned}`;
}

// Równanie Ax+B = 0 (dla c=0 i |Ax+B| ≤ 0)
function solveEqAxPlusB0(A, B) {
  const ax = A === 1 ? "x" : A === -1 ? "-x" : `${A}x`;
  const btxt = B === 0 ? "" : B > 0 ? `+ ${B}` : `- ${Math.abs(B)}`;
  const addOp = B === 0 ? "" : String.raw`\;\Big|\, ${B > 0 ? "-" : "+"}\, ${Math.abs(B)}`;
  const line1 = String.raw`${ax} ${btxt} \;=\; 0${addOp}`;
  const rhs2 = B === 0 ? "0" : (B > 0 ? `${-B}` : `${Math.abs(B)}`);
  const line2 = String.raw`${ax} \;=\; ${rhs2}\;\Big|:\, ${A}`;
  const { n, d } = toFrac(Number(rhs2), A);
  const line3 = String.raw`x \;=\; ${fracToLatex({ n, d })}`;
  return String.raw`\begin{aligned}
${line1}\\[2pt]
${line2}\\[2pt]
${line3}
\end{aligned}`;
}

export function renderExplanation({ data }) {
  const { A, B, alpha, beta, gamma, kind_init, kind, c } = data;

  const Center = ({ children }) => (
    <div style={{ display: "block", width: "100%", minWidth: "100%", alignSelf: "stretch", justifySelf: "stretch" }}>
      <div style={{ margin: "0 auto", display: "table" }}>{children}</div>
    </div>
  );

  const left0 = `${alpha}\\,${absLatex(A,B)} ${beta===0?"":beta>0?`+ ${beta}`:`- ${Math.abs(beta)}`}`;
  const eq1 = `${left0}\\;${SIGN_LATEX[kind_init]}\\;${gamma}`;
  const right2 = gamma - beta; // = alpha * c
  const eq2 = `${alpha}\\,${absLatex(A,B)}\\;${SIGN_LATEX[kind_init]}\\;${right2}`;
  const finalSign = alpha >= 0 ? kind_init : invertSign(kind_init);
  const eq3 = `${absLatex(A,B)}\\;${SIGN_LATEX[finalSign]}\\;${c}`;

  const addOpBeta = beta === 0 ? "" : String.raw`\;\Big|\, ${beta > 0 ? "-" : "+"}\, ${Math.abs(beta)}`;
  const eq1Annotated = `${eq1}${addOpBeta}`;
  const eq2Annotated = String.raw`${eq2}\;\Big|:\, ${alpha}`;

  const ans = solutionForAll({ A, B, c, kind });
  const { lowNum, highNum, lowTex, highTex } = orderedBounds(A, B, c);
  const X0tex = fracToLatex(toFrac(-B, A));

  let solLatex = "";
  if (ans.kind === "between") {
    solLatex = String.raw`x\in ${ans.seg.Lclosed ? "[" : "("}${lowTex},\; ${highTex}${ans.seg.Rclosed ? "]" : ")"}`;
  } else if (ans.kind === "outside") {
    solLatex = String.raw`x\in (-\infty,\; ${lowTex}${kind==="ge"?"]":")"}\ \cup\ ${kind==="ge"?"[":"("}${highTex},\; \infty)`;
  } else if (ans.kind === "point") {
    solLatex = String.raw`x\in \{${X0tex}\}`;
  } else if (ans.kind === "outsideHole") {
    solLatex = String.raw`x\in (-\infty,\; ${X0tex})\ \cup\ (${X0tex},\; \infty)`;
  } else if (ans.kind === "all") {
    solLatex = String.raw`x\in \mathbb{R}`;
  } else {
    solLatex = String.raw`x\in \varnothing`;
  }

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Rozwiązujemy nierówność — upraszczamy i dzielimy (gdy dzielimy przez ujemną liczbę, odwracamy znak).</p>

      <Center>
        <BlockMath math={String.raw`
\begin{aligned}
${eq1Annotated}\\[4pt]
${eq2Annotated}\\[2pt]
${eq3}
\end{aligned}
        `} />
      </Center>

      {alpha < 0 && (
        <div style={{ textAlign: "center", marginTop: "0.25rem" }}>
          <InlineMath math={`\\text{Dzielimy przez liczbę ujemną } ${alpha} \\;\\Rightarrow\\; \\text{ odwracamy znak.}`}/>
        </div>
      )}

      {c < 0 ? (
        <>
          <p><strong>Krok 2:</strong> Po prawej stronie jest liczba ujemna, a
            <em> wartość bezwzględna dowolnego wyrażenia jest zawsze nieujemna (≥ 0)</em>.
            Nieujemna liczba jest zawsze większa (lub większa/równa) od liczby ujemnej,
            więc ta nierówność jest <em>prawdziwa dla każdego</em> <InlineMath math="x" />.
          </p>
        </>
      ) : c === 0 ? (
        <>
          {kind === "le" ? (
            <>
              <p><strong>Krok 2:</strong> Dla <InlineMath math="|\,\cdot\,|\le 0" /> mamy to
                <em> tylko wtedy</em>, gdy wyrażenie pod kreską jest równe 0. Rozwiązujemy
                <InlineMath math="\,Ax+B=0" />:
              </p>
              <Center>
                <BlockMath math={solveEqAxPlusB0(A, B)} />
              </Center>
            </>
          ) : (
            <>
              <p><strong>Krok 2:</strong> Dla <InlineMath math="c=0" />:
                {kind==="lt" ? " brak rozwiązań." :
                 kind==="gt" ? " wszystkie x poza miejscem zerowym." :
                               " nierówność prawdziwa dla każdego x."}
              </p>
              {kind==="gt" && (
                <div style={{ maxWidth: 700, margin: "0.5rem auto" }}>
                  <NumberLineRanges
                    lockPair showZero
                    ranges={[
                      { to: -B/A, rightClosed: false, rightLabelLatex: X0tex },
                      { from: -B/A, leftClosed: false, leftLabelLatex: X0tex },
                    ]}
                  />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <p><strong>Krok 2:</strong> Rozbijamy wartość bezwzględną i rozwiązujemy dwie nierówności liniowe.</p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", alignItems: "start" }}>
            <div>
              <div style={{ textAlign: "center", fontWeight: 600 }}>
                {(kind==="lt"||kind==="le") ? "Lewa (koniunkcja)" : "Warunek 1"}
              </div>
              <Center>
                <BlockMath math={solveLinearSide(A, B, (kind==="le"||kind==="ge") ? "≥" : ">", (kind==="lt"||kind==="le") ? -c :  c)} />
              </Center>
            </div>
            <div>
              <div style={{ textAlign: "center", fontWeight: 600 }}>
                {(kind==="lt"||kind==="le") ? "Prawa (koniunkcja)" : "Warunek 2"}
              </div>
              <Center>
                <BlockMath math={solveLinearSide(A, B, (kind==="le"||kind==="ge") ? "≤" : "<", (kind==="lt"||kind==="le") ?  c : -c)} />
              </Center>
            </div>
          </div>

          <div style={{ maxWidth: 700, margin: "0.5rem auto" }}>
            {(kind==="lt"||kind==="le") ? (
              <TwoRangesOverlap
                showZero lockPair pairFractions={[1/3, 2/3]}
                overlapColorPreset="pink" overlapOpacity={0.4} overlapBlur={2}
                ranges={[
                  { to:   highNum, rightClosed: kind === "le", rightLabelLatex: highTex },
                  { from: lowNum,  leftClosed:  kind === "le", leftLabelLatex:  lowTex  },
                ]}
              />
            ) : (
              <NumberLineRanges
                lockPair showZero
                ranges={[
                  { to:   lowNum,  rightClosed: kind === "ge", rightLabelLatex: lowTex  },
                  { from: highNum, leftClosed:  kind === "ge", leftLabelLatex:  highTex },
                ]}
              />
            )}
          </div>
        </>
      )}

      <p><strong>Krok 3:</strong> Zapisujemy rozwiązanie.</p>
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <InlineMath math={solLatex} />
      </div>
    </div>
  );
}
